/**
 * Hook for transaction form submission logic
 * Extracted from Activity.tsx to improve code organization
 * 
 * This hook handles:
 * - Expense/Income transaction submission (create/update)
 * - Transfer transaction submission (create/update)
 * - Transaction history tracking
 * - Attachment uploads and management
 * - Account balance updates
 */

import { useCallback } from "react";
import { FormEvent } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
  addDoc,
  updateDoc,
  writeBatch,
  runTransaction,
  deleteField,
  increment,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, storage, db } from "../../../firebase";
import { Account, AttachmentHistoryEntry, TransactionCategory, TransactionCategoryGroup, TransactionCustomField, TransactionCustomFieldValues, CustomFieldAttachmentValue, TransactionCustomFieldScopeType } from "../../../types";
import { TransactionWithDate } from "../../../utils/transactionProcessing";
import { deriveNoteAndAttachmentFromCustomFields } from "../../../utils/transactionCustomFieldsDerivation";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { formatCurrency, formatRate, combineDateAndTime } from "../../../utils/transactionUtils";
import { api } from "../../../services/api";
import { invalidateTransactionsCache } from "./useTransactions";

export interface FormData {
  title: string;
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string; // Rate from transaction currency to default currency
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: Record<string, any>;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  /** Expense fee (when workspace allowExpenseFee is true) */
  feeAmount?: string;
  feeAccountId?: string;
}

export interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
  feeAmount?: string;
  feeAccountId?: string;
}

export interface UseTransactionSubmissionParams {
  // State
  workspaceId: string;
  workspaceOwnerId: string | null; // Workspace owner ID (used in dev mode instead of bypass-user)
  formData: FormData;
  transferFormData: TransferFormData;
  editingTransaction: TransactionWithDate | null;
  dialogType: "expense" | "income";
  defaultCurrency: string;
  accounts: Account[];
  expenseCategories: TransactionCategory[];
  incomeCategories: TransactionCategory[];
  categoryGroups: TransactionCategoryGroup[];
  newlyAddedDetailOptions: Record<string, string[]>;
  isSubmitting: boolean;
  isSubmittingTransfer: boolean;
  // State setters
  setIsSubmitting: (submitting: boolean) => void;
  setIsSubmittingTransfer: (submitting: boolean) => void;
  setDialogOpen: (open: boolean) => void;
  setTransferDialogOpen: (open: boolean) => void;
  setEditingTransaction: (transaction: TransactionWithDate | null) => void;
  setFormData: (data: FormData | ((prev: FormData) => FormData)) => void;
  setTransferFormData: (data: TransferFormData | ((prev: TransferFormData) => TransferFormData)) => void;
  setShowTime: (show: boolean) => void;
  setShowTransferTime: (show: boolean) => void;
  setShowTransferFee?: (show: boolean) => void;
  setUploadingAttachment: (uploading: boolean) => void;
  setUploadingTransferAttachment: (uploading: boolean) => void;
  setSnackbarElement: (element: { severity: string; message: string } | null) => void;
  setSnackbarOpen: (open: boolean) => void;
  setNewlyAddedDetailOptions: (options: Record<string, string[]>) => void;
  setDetailInputValues: (values: Record<string, string>) => void;
  setTransactionHasHistory: (updater: (prev: Set<string>) => Set<string>) => void;
  // Helper functions
  getAccountById: (accountId: string | undefined) => Account | undefined;
  getAccountName: (accountId: string | undefined, storedAccountName?: string) => string;
  calculateTransferRate: (fromCurrency: string, toCurrency: string) => number;
  defaultCurrencyRateManuallyEdited: boolean;
  /** Workspace custom fields (for history change labels) */
  transactionCustomFields?: TransactionCustomField[];
  /** When true, expense form can include optional fee (amount + account) */
  allowExpenseFee?: boolean;
  /** When true, transfer form can include optional fee (amount + account). Only affects new transactions. */
  allowTransferFee?: boolean;
  /** Call after successful save so transaction list refetches (e.g. to show new fee with id) */
  onTransactionSaved?: () => void;
}

export interface UseTransactionSubmissionResult {
  handleSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  handleTransferSubmit: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  saveTransactionHistory: (
    transactionId: string,
    oldTransaction: TransactionWithDate,
    newTransactionData: any
  ) => Promise<void>;
  saveCreationHistory: (transactionId: string) => Promise<void>;
}

/**
 * Hook for transaction submission logic
 */
export const useTransactionSubmission = (
  params: UseTransactionSubmissionParams
): UseTransactionSubmissionResult => {
  const {
    workspaceId,
    workspaceOwnerId,
    formData,
    transferFormData,
    editingTransaction,
    dialogType,
    defaultCurrency,
    accounts,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    newlyAddedDetailOptions,
    isSubmitting,
    isSubmittingTransfer,
    setIsSubmitting,
    setIsSubmittingTransfer,
    setDialogOpen,
    setTransferDialogOpen,
    setEditingTransaction,
    setFormData,
    setTransferFormData,
    setShowTime,
    setShowTransferTime,
    setShowTransferFee,
    setUploadingAttachment,
    setUploadingTransferAttachment,
    setSnackbarElement,
    setSnackbarOpen,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    setTransactionHasHistory,
    getAccountById,
    getAccountName,
    calculateTransferRate,
    defaultCurrencyRateManuallyEdited,
    transactionCustomFields = [],
    allowExpenseFee = false,
    allowTransferFee = false,
    onTransactionSaved,
  } = params;

  // Save creation history helper
  const saveCreationHistory = useCallback(async (transactionId: string): Promise<void> => {
    if (!workspaceId) {
      return;
    }

    if (!transactionId) {
      return;
    }

    // Get userId - use workspace owner ID in bypass auth mode
    let userId = auth.currentUser?.uid;
    if (!userId && isBypassAuthEnabled() && workspaceOwnerId) {
      userId = workspaceOwnerId;
    }
    if (!userId) {
      return; // Skip history if no user ID available
    }

    try {
      const historyRef = collection(db, "workspaces", workspaceId, "transactions", transactionId, "history");
      await addDoc(historyRef, {
        userId,
        timestamp: serverTimestamp(),
        changes: ["Created"],
      });
    } catch (error) {
      console.error("Error saving creation history:", error);
      console.error("Transaction ID:", transactionId);
      console.error("Workspace ID:", workspaceId);
    }
  }, [workspaceId, workspaceOwnerId]);

  // Save transaction history helper
  const saveTransactionHistory = useCallback(async (
    transactionId: string,
    oldTransaction: TransactionWithDate,
    newTransactionData: any
  ): Promise<void> => {
    if (!workspaceId) return;

    // Get userId - use workspace owner ID in bypass auth mode
    let userId = auth.currentUser?.uid;
    if (!userId && isBypassAuthEnabled() && workspaceOwnerId) {
      userId = workspaceOwnerId;
    }
    if (!userId) {
      return; // Skip history if no user ID available
    }

    const changes: string[] = [];

    // Track account changes (for expense/income)
    if (newTransactionData.accountId !== undefined && oldTransaction.accountId !== newTransactionData.accountId) {
      const oldAccountName = getAccountName(oldTransaction.accountId, oldTransaction.accountName);
      const newAccountName = getAccountName(newTransactionData.accountId, newTransactionData.accountName);
      changes.push(`Account changed from ${oldAccountName || ""} to ${newAccountName || ""}`);
    }

    // Track amount changes
    if (newTransactionData.amount !== undefined) {
      const oldAmount = Number(oldTransaction.amount) || 0;
      const newAmount = Number(newTransactionData.amount) || 0;
      const amountDiff = Math.abs(oldAmount - newAmount);
      console.log("Amount comparison:", { oldAmount, newAmount, amountDiff, threshold: 0.01 });
      if (amountDiff > 0.01) {
        changes.push(`Amount changed from ${oldAmount.toLocaleString()} to ${newAmount.toLocaleString()}`);
      }
    }

    // Track currency changes
    if (newTransactionData.currency !== undefined && oldTransaction.currency !== newTransactionData.currency) {
      changes.push(`Currency changed from ${oldTransaction.currency || defaultCurrency} to ${newTransactionData.currency || defaultCurrency}`);
      
      // If currency changed and rate exists, show recalculated amount
      if (newTransactionData.rate && newTransactionData.amount) {
        const recalculatedAmount = (newTransactionData.amount || 0) * (newTransactionData.rate || 1);
        const account = accounts.find((acc) => acc.id === newTransactionData.accountId);
        const accountCurrency = account?.currency || defaultCurrency;
        changes.push(`Recalculated ${formatCurrency(recalculatedAmount, accountCurrency)}`);
      }
    }

    // Track rate changes
    if (newTransactionData.rate !== undefined || newTransactionData.transferRate !== undefined) {
      const oldRate = oldTransaction.rate ?? oldTransaction.transferRate;
      const newRate = newTransactionData.rate ?? newTransactionData.transferRate;
      
      // Check if currencies are the same (rate should be 1)
      const oldCurrency = oldTransaction.currency;
      const newCurrency = newTransactionData.currency ?? oldCurrency;
      const currenciesSame = oldCurrency && newCurrency && oldCurrency === newCurrency;
      
      // Normalize: if currencies are the same, rate should be 1 (treat undefined as 1 for same currency)
      const normalizedOldRate = (oldRate === undefined && currenciesSame) ? 1 : oldRate;
      const normalizedNewRate = (newRate === undefined && currenciesSame) ? 1 : newRate;
      
      // Only track rate change if:
      // 1. Both rates exist (or normalized) and are actually different
      // 2. Don't track undefined -> 1 when currencies are the same (expected behavior)
      if (normalizedOldRate !== undefined && normalizedNewRate !== undefined) {
        if (Math.abs(normalizedOldRate - normalizedNewRate) > 0.0001) {
          // Only show change if it's not just setting rate to 1 for same currency when old was undefined
          if (!(oldRate === undefined && normalizedNewRate === 1 && currenciesSame)) {
            changes.push(`Rate ${formatRate(normalizedOldRate)} changed to ${formatRate(normalizedNewRate)}`);
          }
        }
      } else if (oldRate === undefined && newRate !== undefined && newRate !== 1) {
        // Old rate was undefined, new rate is set and it's not 1 (different currencies)
        changes.push(`Rate set to ${formatRate(newRate)}`);
      } else if (oldRate !== undefined && newRate === undefined && oldRate !== 1) {
        // Rate was removed (and it wasn't just 1 for same currency)
        changes.push(`Rate ${formatRate(oldRate)} removed`);
      }
    }

    // Track default currency rate changes
    if (newTransactionData.defaultCurrencyRate !== undefined) {
      const oldDefaultRate = oldTransaction.defaultCurrencyRate;
      const newDefaultRate = newTransactionData.defaultCurrencyRate;
      
      // Only track if rate actually changed (more than 0.0001 difference)
      if (oldDefaultRate !== undefined && oldDefaultRate !== null && newDefaultRate !== undefined && newDefaultRate !== null) {
        if (Math.abs(oldDefaultRate - newDefaultRate) > 0.0001) {
          changes.push(`Default currency rate ${formatRate(oldDefaultRate)} changed to ${formatRate(newDefaultRate)}`);
          // Also show recalculated amount if available
          if (newTransactionData.amountInDefaultCurrency !== undefined && newTransactionData.amount !== undefined) {
            const recalculatedAmount = newTransactionData.amountInDefaultCurrency;
            changes.push(`Recalculated amount in default currency: ${formatCurrency(recalculatedAmount, defaultCurrency)}`);
          }
        }
      } else if ((oldDefaultRate === undefined || oldDefaultRate === null) && newDefaultRate !== undefined && newDefaultRate !== null) {
        // Default currency rate was added
        changes.push(`Default currency rate set to ${formatRate(newDefaultRate)}`);
      } else if (oldDefaultRate !== undefined && oldDefaultRate !== null && (newDefaultRate === undefined || newDefaultRate === null)) {
        // Default currency rate was removed
        changes.push(`Default currency rate ${formatRate(oldDefaultRate)} removed`);
      }
    }

    // Track date and time changes
    if (newTransactionData.date !== undefined) {
      const oldDate = oldTransaction.date instanceof Date ? oldTransaction.date : new Date(oldTransaction.date);
      const newDate = newTransactionData.date instanceof Timestamp 
        ? newTransactionData.date.toDate() 
        : new Date(newTransactionData.date);
      
      // Check if date changed (ignoring time)
      const oldDateOnly = new Date(oldDate.getFullYear(), oldDate.getMonth(), oldDate.getDate());
      const newDateOnly = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
      const dateChanged = oldDateOnly.getTime() !== newDateOnly.getTime();
      
      // Check if time changed (or was added/removed)
      const oldTime = oldDate.getHours() * 60 + oldDate.getMinutes(); // Minutes since midnight
      const newTime = newDate.getHours() * 60 + newDate.getMinutes();
      const oldHadTime = oldTime !== 0; // 00:00 means no time was set
      const newHasTime = newTime !== 0;
      const timeChanged = oldTime !== newTime;
      
      if (dateChanged) {
        const oldDateStr = oldDate.toLocaleDateString();
        const newDateStr = newDate.toLocaleDateString();
        changes.push(`Date changed from ${oldDateStr} to ${newDateStr}`);
      }
      
      // Track time changes separately
      if (timeChanged) {
        if (!oldHadTime && newHasTime) {
          // Time was added
          const timeStr = `${String(newDate.getHours()).padStart(2, "0")}:${String(newDate.getMinutes()).padStart(2, "0")}`;
          changes.push(`Time added: ${timeStr}`);
        } else if (oldHadTime && !newHasTime) {
          // Time was removed
          const oldTimeStr = `${String(oldDate.getHours()).padStart(2, "0")}:${String(oldDate.getMinutes()).padStart(2, "0")}`;
          changes.push(`Time removed: ${oldTimeStr}`);
        } else if (oldHadTime && newHasTime) {
          // Time was changed
          const oldTimeStr = `${String(oldDate.getHours()).padStart(2, "0")}:${String(oldDate.getMinutes()).padStart(2, "0")}`;
          const newTimeStr = `${String(newDate.getHours()).padStart(2, "0")}:${String(newDate.getMinutes()).padStart(2, "0")}`;
          changes.push(`Time changed from ${oldTimeStr} to ${newTimeStr}`);
        }
      }
    }

    // Track category changes (for expense/income)
    if (newTransactionData.category !== undefined && oldTransaction.category !== newTransactionData.category) {
      changes.push(`Category changed from ${oldTransaction.category || ""} to ${newTransactionData.category || ""}`);
    }

    // Track title changes
    if (newTransactionData.title !== undefined && oldTransaction.title !== newTransactionData.title) {
      changes.push(`Title changed from "${oldTransaction.title || ""}" to "${newTransactionData.title || ""}"`);
    }

    // Track note changes
    if (newTransactionData.note !== undefined && oldTransaction.note !== newTransactionData.note) {
      const oldNote = oldTransaction.note || "";
      const newNote = newTransactionData.note || "";
      if (oldNote !== newNote) {
        if (oldNote && newNote) {
          changes.push(`Note changed from "${oldNote}" to "${newNote}"`);
        } else if (oldNote && !newNote) {
          changes.push(`Note removed: "${oldNote}"`);
        } else if (!oldNote && newNote) {
          changes.push(`Note added: "${newNote}"`);
        }
      }
    }

    // Track account changes for transfers
    if (newTransactionData.fromAccountId !== undefined || newTransactionData.toAccountId !== undefined) {
      const oldFromAccountName = getAccountName(oldTransaction.fromAccountId, oldTransaction.fromAccountName);
      const oldToAccountName = getAccountName(oldTransaction.toAccountId, oldTransaction.toAccountName);
      const newFromAccountName = getAccountName(newTransactionData.fromAccountId, newTransactionData.fromAccountName);
      const newToAccountName = getAccountName(newTransactionData.toAccountId, newTransactionData.toAccountName);
      
      if (oldTransaction.fromAccountId !== newTransactionData.fromAccountId) {
        changes.push(`From account changed from ${oldFromAccountName || ""} to ${newFromAccountName || ""}`);
      }
      if (oldTransaction.toAccountId !== newTransactionData.toAccountId) {
        changes.push(`To account changed from ${oldToAccountName || ""} to ${newToAccountName || ""}`);
      }
    }

    // Track attachment changes (skip if same file already recorded as custom field change to avoid duplicate history lines)
    const urlInCustomValues = (url: string, custom: TransactionCustomFieldValues | undefined): boolean => {
      if (!url || !custom) return false;
      return Object.values(custom).some(
        (v) => typeof v === "object" && v != null && "url" in v && (v as CustomFieldAttachmentValue).url === url
      );
    };
    if (newTransactionData.attachmentUrl !== undefined) {
      const oldUrl = oldTransaction.attachmentUrl || "";
      const newUrl = newTransactionData.attachmentUrl || "";
      const oldName = oldTransaction.attachmentName || "";
      const newName = newTransactionData.attachmentName || "";
      const newCustom = (newTransactionData.customFieldValues || {}) as TransactionCustomFieldValues;
      const oldCustom = (oldTransaction.customFieldValues || {}) as TransactionCustomFieldValues;

      if (oldUrl && !newUrl) {
        if (!urlInCustomValues(oldUrl, oldCustom)) changes.push(`Attachment removed: ${oldName}`);
      } else if (!oldUrl && newUrl) {
        if (!urlInCustomValues(newUrl, newCustom)) changes.push(`Attachment added: ${newName}`);
      } else if (oldUrl && newUrl && oldUrl !== newUrl) {
        if (!urlInCustomValues(newUrl, newCustom)) changes.push(`Attachment replaced: ${oldName} → ${newName}`);
      }
    }

    // Track details changes
    if (newTransactionData.details !== undefined) {
      const oldDetails = oldTransaction.details || {};
      const newDetails = newTransactionData.details || {};
      
      // Check for added details
      Object.keys(newDetails).forEach((key) => {
        if (!(key in oldDetails)) {
          changes.push(`Detail "${key}" added: ${newDetails[key]}`);
        } else if (oldDetails[key] !== newDetails[key]) {
          changes.push(`Detail "${key}" changed from "${oldDetails[key]}" to "${newDetails[key]}"`);
        }
      });
      
      // Check for removed details
      Object.keys(oldDetails).forEach((key) => {
        if (!(key in newDetails)) {
          changes.push(`Detail "${key}" removed: ${oldDetails[key]}`);
        }
      });
    }

    // Track custom field changes
    const oldCustom = (oldTransaction.customFieldValues || {}) as TransactionCustomFieldValues;
    const newCustom = (newTransactionData.customFieldValues || {}) as TransactionCustomFieldValues;
    const getFieldLabel = (fieldId: string) => transactionCustomFields.find((f) => f.id === fieldId)?.label ?? fieldId;

    const allCustomKeys = new Set([...Object.keys(oldCustom), ...Object.keys(newCustom)]);
    allCustomKeys.forEach((fieldId) => {
      const oldVal = oldCustom[fieldId];
      const newVal = newCustom[fieldId];
      const label = getFieldLabel(fieldId);
      const oldText = typeof oldVal === "string" ? oldVal : (oldVal && typeof oldVal === "object" && "name" in oldVal ? (oldVal as CustomFieldAttachmentValue).name : "");
      const newText = typeof newVal === "string" ? newVal : (newVal && typeof newVal === "object" && "name" in newVal ? (newVal as CustomFieldAttachmentValue).name : "");
      const oldIsAttachment = oldVal && typeof oldVal === "object" && "url" in oldVal;
      const newIsAttachment = newVal && typeof newVal === "object" && "url" in newVal;

      if (oldVal === undefined && newVal !== undefined) {
        if (newIsAttachment) {
          changes.push(`Custom field "${label}" added: ${newText}`);
        } else {
          changes.push(`Custom field "${label}" added: ${newText}`);
        }
      } else if (oldVal !== undefined && newVal === undefined) {
        if (oldIsAttachment) {
          changes.push(`Custom field "${label}" removed: ${(oldVal as CustomFieldAttachmentValue).name}`);
        } else {
          changes.push(`Custom field "${label}" removed: ${oldText}`);
        }
      } else if (oldVal !== undefined && newVal !== undefined) {
        if (oldIsAttachment && newIsAttachment) {
          const oName = (oldVal as CustomFieldAttachmentValue).name;
          const nName = (newVal as CustomFieldAttachmentValue).name;
          if (oName !== nName || (oldVal as CustomFieldAttachmentValue).url !== (newVal as CustomFieldAttachmentValue).url) {
            changes.push(`Custom field "${label}" replaced: ${oName} → ${nName}`);
          }
        } else if (!oldIsAttachment && newIsAttachment) {
          changes.push(`Custom field "${label}" changed to attachment: ${newText}`);
        } else if (oldIsAttachment && !newIsAttachment) {
          changes.push(`Custom field "${label}" changed from attachment to: ${newText}`);
        } else if (oldText !== newText) {
          changes.push(`Custom field "${label}" changed from "${oldText}" to "${newText}"`);
        }
      }
    });

    // Only save history if there are changes
    if (changes.length === 0) {
      // No changes detected, skipping history save
      return;
    }

    try {
      const historyRef = collection(db, "workspaces", workspaceId, "transactions", transactionId, "history");
      const historyData: any = {
        userId,
        timestamp: serverTimestamp(),
        changes: changes,
      };
      // Only include attachment/customFieldValues in this history entry when something actually changed,
      // so the history UI doesn't show "Expense DOC: file.pdf" on every update when only note/amount etc. changed.
      const hasMainAttachmentChange = changes.some(
        (c) => c.startsWith("Attachment added:") || c.startsWith("Attachment removed:") || c.startsWith("Attachment replaced:")
      );
      const hasCustomFieldChange = changes.some((c) => c.startsWith("Custom field \""));
      if (hasMainAttachmentChange && newTransactionData.attachmentUrl !== undefined && newTransactionData.attachmentUrl !== null && newTransactionData.attachmentUrl !== "") {
        historyData.attachmentUrl = newTransactionData.attachmentUrl;
      }
      if (hasMainAttachmentChange && newTransactionData.attachmentName !== undefined && newTransactionData.attachmentName !== null && newTransactionData.attachmentName !== "") {
        historyData.attachmentName = newTransactionData.attachmentName;
      }
      if (hasCustomFieldChange && newTransactionData.customFieldValues !== undefined && Object.keys(newTransactionData.customFieldValues).length > 0) {
        historyData.customFieldValues = newTransactionData.customFieldValues;
      }
      await addDoc(historyRef, historyData);
      
      // Update the transactionHasHistory set
      setTransactionHasHistory((prev) => new Set(prev).add(transactionId));
    } catch (error) {
      console.error("Error saving transaction history:", error);
    }
  }, [workspaceId, workspaceOwnerId, defaultCurrency, accounts, getAccountName, formatCurrency, formatRate, setTransactionHasHistory, transactionCustomFields]);

  // Handle expense/income submission
  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }
    
    // Allow loading in demo mode, bypass auth mode, or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    if (!formData.accountId) {
      alert("Please select an account");
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) {
      alert("Please enter a valid amount");
      return;
    }

    const account = getAccountById(formData.accountId);
    if (!account) {
      alert("Selected account not found");
      return;
    }

    // Sync newly added detail options to category group details before creating/updating transaction
    if (formData.category && Object.keys(newlyAddedDetailOptions).length > 0) {
      try {
        // Find the selected category and its group
        const selectedCategory = [...expenseCategories, ...incomeCategories].find(
          cat => cat.name === formData.category
        );
        
        if (selectedCategory && selectedCategory.groupId) {
          const group = categoryGroups.find(g => g.id === selectedCategory.groupId);
          
          // Ensure group exists and has valid name and ID
          if (group && group.id && group.name && group.details) {
            // Update group details with newly added options
            const updatedDetails = group.details.map(detail => {
              const newOptions = newlyAddedDetailOptions[detail.name];
              if (newOptions && newOptions.length > 0 && detail.type === "list") {
                // Merge new options with existing listItems
                const existingItems = detail.listItems || [];
                const mergedItems = [...existingItems, ...newOptions];
                // Remove duplicates
                const uniqueItems = Array.from(new Set(mergedItems));
                return {
                  ...detail,
                  listItems: uniqueItems,
                };
              }
              return detail;
            });

            // Update the group via API (only update details, not name or description)
            await api.updateGroup(workspaceId, group.id, undefined, undefined, updatedDetails);
            
            // Refresh category groups to get updated details
            const [expenseGroups, incomeGroups] = await Promise.all([
              api.getGroups(workspaceId, "expense"),
              api.getGroups(workspaceId, "income"),
            ]);
            // Category groups are managed by useCategories hook, no need to set them here
            console.log("Category groups synced:", [...expenseGroups, ...incomeGroups].length);
          }
        }
      } catch (error) {
        console.error("Error syncing new detail options:", error);
        // Don't block transaction creation if sync fails
      }
    }

    // DEMO MODE: Block all write operations - demo mode is read-only
    if (isDemoMode()) {
      setSnackbarElement({
        severity: "info",
        message: "Demo mode: Transactions cannot be saved. Please log in to save transactions.",
      });
      setSnackbarOpen(true);
      setDialogOpen(false);
      setEditingTransaction(null);
      setShowTime(false);
      setFormData({
        title: "",
        category: "",
        amount: "",
        currency: defaultCurrency,
        rate: "",
        defaultCurrencyRate: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        accountId: "",
        accountGroup: "",
        feeAmount: undefined,
        feeAccountId: undefined,
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Ensure user is in workspace members subcollection before saving transaction
      // Skip this check in dev mode (bypass auth enabled)
      if (auth.currentUser && workspaceId && !isBypassAuthEnabled()) {
        const userId = auth.currentUser.uid;
        const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
        const memberSnap = await getDoc(memberRef);
        
        if (!memberSnap.exists()) {
          // Check if user is the workspace owner
          const workspaceRef = doc(db, "workspaces", workspaceId);
          const workspaceSnap = await getDoc(workspaceRef);
          const workspaceData = workspaceSnap.data();
          const isOwner = workspaceSnap.exists() && workspaceData?.ownerId === userId;
          
          if (isOwner) {
            // Add owner to members subcollection
            await setDoc(memberRef, {
              role: "owner",
              joinedAt: serverTimestamp(),
            });
            
            // Verify it was added
            const verifySnap = await getDoc(memberRef);
            if (!verifySnap.exists()) {
              throw new Error("Failed to add user to workspace members. Please try again.");
            }
            
            // Added workspace owner to members subcollection before transaction save
          } else {
            throw new Error("User is not a member of this workspace. Please ask the workspace owner to add you as a member.");
          }
        }
      }
      
      const derived = deriveNoteAndAttachmentFromCustomFields(
        transactionCustomFields,
        dialogType,
        formData.customFieldValues
      );

      const batch = writeBatch(db);
      const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
      const accountRef = doc(db, "workspaces", workspaceId, "accounts", account.id);
      let transactionRef: ReturnType<typeof doc> | null = null;
      let transactionDataForHistory: any = null;

      if (editingTransaction) {
        // Reverse the old transaction's effect on account balance
        const oldAmount = editingTransaction.amount || 0;
        const oldIsIncome = editingTransaction.isIncome || false;
        const oldAccountId = editingTransaction.accountId || "";
        
        // Calculate old amount in account currency (handle currency conversion)
        let oldAmountInAccountCurrency = oldAmount;
        if (oldAccountId) {
          const oldAccount = getAccountById(oldAccountId);
          if (oldAccount) {
            const oldAccountCurrency = oldAccount.currency || defaultCurrency;
            const oldExpenseCurrency = editingTransaction.currency || oldAccountCurrency;
            if (oldExpenseCurrency !== oldAccountCurrency && editingTransaction.rate) {
              oldAmountInAccountCurrency = oldAmount * editingTransaction.rate;
            }
          }
        }
        
        // Handle currency conversion for new transaction
        const accountCurrency = account.currency || defaultCurrency;
        const expenseCurrency = formData.currency || accountCurrency;
        const currenciesDiffer = expenseCurrency !== accountCurrency;
        
        let amountInAccountCurrency = amount;
        let rate: number;
        
        if (currenciesDiffer) {
          if (!formData.rate || formData.rate.trim() === "") {
            alert("Please enter an exchange rate for currency conversion");
            return;
          }
          rate = parseFloat(formData.rate);
          if (isNaN(rate) || rate <= 0) {
            alert("Please enter a valid exchange rate");
            return;
          }
          // Convert expense amount to account currency
          amountInAccountCurrency = amount * rate;
        } else {
          // Same currency - rate is always 1
          rate = 1;
        }
        
        // Update account balances using atomic increments to prevent race conditions
        if (oldAccountId && oldAccountId !== formData.accountId) {
          // Account changed - reverse old account, apply new account
          const oldAccount = getAccountById(oldAccountId);
          if (oldAccount) {
            const oldAccountRef = doc(db, "workspaces", workspaceId, "accounts", oldAccount.id);
            // Reverse old transaction effect on old account
            if (oldIsIncome) {
              batch.update(oldAccountRef, { amount: increment(-oldAmountInAccountCurrency) });
            } else {
              batch.update(oldAccountRef, { amount: increment(oldAmountInAccountCurrency) });
            }
          }
          // Apply new transaction effect on new account
          if (dialogType === "income") {
            batch.update(accountRef, { amount: increment(amountInAccountCurrency) });
          } else {
            batch.update(accountRef, { amount: increment(-amountInAccountCurrency) });
          }
        } else if (oldAccountId === formData.accountId && oldAccountId) {
          // Same account - calculate net change and apply it atomically
          let netChange: number;
          if (oldIsIncome && dialogType === "income") {
            // Both income: net = new - old
            netChange = amountInAccountCurrency - oldAmountInAccountCurrency;
          } else if (oldIsIncome && dialogType === "expense") {
            // Old income, new expense: net = -new - old = -(new + old)
            netChange = -(amountInAccountCurrency + oldAmountInAccountCurrency);
          } else if (!oldIsIncome && dialogType === "income") {
            // Old expense, new income: net = new + old
            netChange = amountInAccountCurrency + oldAmountInAccountCurrency;
          } else {
            // Both expense: net = old - new (since expenses are negative)
            netChange = oldAmountInAccountCurrency - amountInAccountCurrency;
          }
          batch.update(accountRef, { amount: increment(netChange) });
        } else {
          // No old account (shouldn't happen when editing, but handle gracefully)
          if (dialogType === "income") {
            batch.update(accountRef, { amount: increment(amountInAccountCurrency) });
          } else {
            batch.update(accountRef, { amount: increment(-amountInAccountCurrency) });
          }
        }
        
        // Calculate amount in default currency and defaultCurrencyRate
        // For edits: recalculate amountInDefaultCurrency if defaultCurrencyRate changed, otherwise preserve original
        // For new transactions: calculate and store
        // Field stores account-to-default rate, but we need expense-to-default rate for storage
        let amountInDefaultCurrency: number;
        let defaultCurrencyRate: number | undefined;
        
        if (expenseCurrency !== defaultCurrency) {
          let accountToDefaultRate: number;
          const parsedFormRate = formData.defaultCurrencyRate?.trim() ? parseFloat(formData.defaultCurrencyRate) : NaN;
          if (!formData.defaultCurrencyRate || formData.defaultCurrencyRate.trim() === "") {
            alert("Please enter a default currency exchange rate for currency conversion");
            return;
          }
          if (!isNaN(parsedFormRate) && parsedFormRate > 0 && parsedFormRate >= 0.0001 && parsedFormRate <= 10000) {
            accountToDefaultRate = parsedFormRate;
          } else {
            // Invalid rate (e.g. "0", "?", stale data) - fall back to calculated rate instead of blocking
            accountToDefaultRate = accountCurrency !== defaultCurrency 
              ? calculateTransferRate(accountCurrency, defaultCurrency)
              : 1;
          }
          
          // Field stores account-to-default rate, convert to expense-to-default rate
          // expenseAmount * expenseToDefaultRate = defaultAmount
          // accountAmount * accountToDefaultRate = defaultAmount
          // accountAmount = expenseAmount * exchangeRate
          // So: expenseAmount * expenseToDefaultRate = expenseAmount * exchangeRate * accountToDefaultRate
          // Therefore: expenseToDefaultRate = exchangeRate * accountToDefaultRate
          const expenseToDefaultRate = rate * accountToDefaultRate;
          
          // Check if defaultCurrencyRate changed from the original transaction
          // Compare expense-to-default rates (what's stored in the transaction)
          const oldDefaultCurrencyRate = editingTransaction.defaultCurrencyRate;
          const rateChanged = oldDefaultCurrencyRate === undefined || 
                             oldDefaultCurrencyRate === null ||
                             Math.abs(oldDefaultCurrencyRate - expenseToDefaultRate) > 0.0001;
          
          // If rate changed OR transaction didn't have amountInDefaultCurrency, recalculate
          if (rateChanged || editingTransaction.amountInDefaultCurrency === undefined || editingTransaction.amountInDefaultCurrency === null) {
            // Calculate amount in default currency: expenseAmount -> accountAmount -> defaultAmount
            amountInDefaultCurrency = amountInAccountCurrency * accountToDefaultRate;
          } else {
            // Rate didn't change, preserve original amountInDefaultCurrency (historical accuracy)
            amountInDefaultCurrency = editingTransaction.amountInDefaultCurrency;
          }
          
          // Store expense-to-default rate for the transaction
          defaultCurrencyRate = expenseToDefaultRate;
        } else {
          // Same currency as default
          defaultCurrencyRate = 1;
          amountInDefaultCurrency = amount;
        }
        
        // Update transaction
        const transactionData: any = {
          userId: auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : "demo-user"),
          title: formData.title || formData.category || "",
          category: formData.category,
          type: dialogType === "income" ? "income" : "expense",
          amount: amount, // Store original amount in expense currency
          currency: expenseCurrency,
          rate: rate, // Always include rate (1 for same currency, conversion rate otherwise)
          amountInDefaultCurrency: amountInDefaultCurrency, // Store amount in default currency (preserve original if editing)
          defaultCurrencyAtTime: editingTransaction.defaultCurrencyAtTime || defaultCurrency, // Preserve original default currency if editing
          isIncome: dialogType === "income",
          date: Timestamp.fromDate(new Date(combineDateAndTime(formData.date, formData.time))),
          note: derived.note || "",
          accountId: formData.accountId,
          accountName: account.accountName, // Store account name for deleted accounts
          createdAt: editingTransaction.createdAt,
        };
        
        // Only include defaultCurrencyRate if currencies differ (Firestore rejects undefined values)
        if (expenseCurrency !== defaultCurrency && defaultCurrencyRate != null && defaultCurrencyRate > 0) {
          transactionData.defaultCurrencyRate = defaultCurrencyRate;
        } else if (expenseCurrency === defaultCurrency && editingTransaction.defaultCurrencyRate !== undefined) {
          // Remove defaultCurrencyRate if currency changed to default currency
          transactionData.defaultCurrencyRate = deleteField();
        }
        
        // Handle details - track if details were removed for history
        let detailsRemoved = false;
        if (formData.details && Object.keys(formData.details).length > 0) {
          transactionData.details = formData.details;
        } else if (editingTransaction.details) {
          // Remove details if they were removed from form
          transactionData.details = deleteField();
          detailsRemoved = true;
        }
        
        // Attachment fields derived from first attachment custom field
        if (derived.attachmentUrl) {
          transactionData.attachmentUrl = derived.attachmentUrl;
          transactionData.attachmentName = derived.attachmentName || "";
          transactionData.attachmentType = derived.attachmentType || "";
        } else if (editingTransaction.attachmentUrl) {
          transactionData.attachmentUrl = deleteField();
          transactionData.attachmentName = deleteField();
          transactionData.attachmentType = deleteField();
        }
        // Custom field values (pending files uploaded after batch)
        if (formData.customFieldValues && Object.keys(formData.customFieldValues).length > 0) {
          transactionData.customFieldValues = { ...formData.customFieldValues };
        } else if (editingTransaction.customFieldValues) {
          transactionData.customFieldValues = deleteField();
        }
        
        transactionRef = doc(db, "workspaces", workspaceId, "transactions", editingTransaction.id);
        // Set transactionDataForHistory - include ALL fields for proper history comparison
        transactionDataForHistory = {
          title: formData.title || formData.category || "",
          category: formData.category,
          amount: amount,
          currency: expenseCurrency,
          rate: rate,
          amountInDefaultCurrency: amountInDefaultCurrency,
          date: Timestamp.fromDate(new Date(combineDateAndTime(formData.date, formData.time))),
          note: derived.note || "",
          accountId: formData.accountId,
          accountName: account.accountName,
        };
        // Only include defaultCurrencyRate if currencies differ (for comparison purposes)
        if (expenseCurrency !== defaultCurrency && defaultCurrencyRate != null && defaultCurrencyRate > 0) {
          transactionDataForHistory.defaultCurrencyRate = defaultCurrencyRate;
        }
        // Include details if present, or undefined if removed
        if (formData.details && Object.keys(formData.details).length > 0) {
          transactionDataForHistory.details = formData.details;
        } else if (editingTransaction.details) {
          // Details were removed
          transactionDataForHistory.details = undefined;
        }
        transactionDataForHistory.attachmentUrl = derived.attachmentUrl ?? editingTransaction.attachmentUrl ?? null;
        transactionDataForHistory.attachmentName = derived.attachmentName ?? editingTransaction.attachmentName ?? null;
        // Include custom field values for history comparison
        if (formData.customFieldValues && Object.keys(formData.customFieldValues).length > 0) {
          transactionDataForHistory.customFieldValues = { ...formData.customFieldValues };
        } else if (editingTransaction.customFieldValues) {
          transactionDataForHistory.customFieldValues = undefined;
        } else {
          transactionDataForHistory.customFieldValues = editingTransaction.customFieldValues || undefined;
        }
        // Add updatedAt when editing
        transactionData.updatedAt = serverTimestamp();
        batch.update(transactionRef, transactionData);

        // Expense fee (when workspace allows): create/update/remove linked fee transaction
        if (dialogType === "expense" && allowExpenseFee) {
          const feeQuery = query(
            transactionsRef,
            where("expenseFeeFor", "==", editingTransaction.id),
            where("type", "==", "expense")
          );
          const feeQuerySnapshot = await getDocs(feeQuery);
          const existingFeeDoc = feeQuerySnapshot.empty ? null : feeQuerySnapshot.docs[0];
          const existingFeeId = existingFeeDoc?.id ?? null;
          const oldFeeAmount = (existingFeeDoc?.data()?.amount as number) || 0;
          const oldFeeAccountId = (existingFeeDoc?.data()?.accountId as string) || null;

          const newFeeAmount = formData.feeAmount && formData.feeAccountId
            ? parseFloat(formData.feeAmount)
            : 0;
          const hasNewFee = !isNaN(newFeeAmount) && newFeeAmount > 0 && formData.feeAccountId;
          const feeAccount = hasNewFee ? getAccountById(formData.feeAccountId) : null;

          if (existingFeeId && (oldFeeAmount > 0 || oldFeeAccountId)) {
            // Reverse old fee balance
            const oldFeeAccountRef = doc(db, "workspaces", workspaceId, "accounts", oldFeeAccountId!);
            batch.update(oldFeeAccountRef, { amount: increment(oldFeeAmount) });
          }

          if (hasNewFee && feeAccount) {
            const feeAccountRef = doc(db, "workspaces", workspaceId, "accounts", feeAccount.id);
            batch.update(feeAccountRef, { amount: increment(-newFeeAmount) });
            const feeTransactionData: any = {
              userId: auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : "demo-user"),
              title: "Expense Fee",
              category: "Expense Fee",
              type: "expense",
              amount: newFeeAmount,
              currency: feeAccount.currency,
              rate: 1,
              amountInDefaultCurrency: newFeeAmount,
              defaultCurrencyAtTime: defaultCurrency,
              isIncome: false,
              date: Timestamp.fromDate(new Date(combineDateAndTime(formData.date, formData.time))),
              note: `Fee for expense: ${formData.title || formData.category || ""}`,
              accountId: feeAccount.id,
              accountName: feeAccount.accountName,
              expenseFeeFor: editingTransaction.id,
            };
            if (existingFeeId) {
              feeTransactionData.createdAt = existingFeeDoc!.data()?.createdAt ?? serverTimestamp();
              feeTransactionData.updatedAt = serverTimestamp();
              batch.update(doc(db, "workspaces", workspaceId, "transactions", existingFeeId), feeTransactionData);
            } else {
              feeTransactionData.createdAt = serverTimestamp();
              const feeRef = doc(transactionsRef);
              feeTransactionData.id = feeRef.id;
              const feeSettingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
              const feeSettingsSnap = await getDoc(feeSettingsRef);
              const feeSettingsData = feeSettingsSnap.data();
              if (feeSettingsData && typeof feeSettingsData.transactionIdPrefix === "string" && feeSettingsData.transactionIdPrefix.trim() !== "") {
                const feeSeq = typeof feeSettingsData.transactionIdNextSequence === "number" ? feeSettingsData.transactionIdNextSequence : 1;
                batch.update(feeSettingsRef, { transactionIdNextSequence: feeSeq + 1 });
                feeTransactionData.friendlyIdSequence = feeSeq;
              }
              batch.set(feeRef, feeTransactionData);
            }
          } else if (existingFeeId) {
            batch.delete(doc(db, "workspaces", workspaceId, "transactions", existingFeeId));
          }
        }
      } else {
        // New transaction - allow negative balances
        // Handle currency conversion
        const accountCurrency = account.currency || defaultCurrency;
        const expenseCurrency = formData.currency || accountCurrency;
        const currenciesDiffer = expenseCurrency !== accountCurrency;
        
        let amountInAccountCurrency = amount;
        let rate: number;
        
        if (currenciesDiffer) {
          if (!formData.rate || formData.rate.trim() === "") {
            alert("Please enter an exchange rate for currency conversion");
            return;
          }
          rate = parseFloat(formData.rate);
          if (isNaN(rate) || rate <= 0) {
            alert("Please enter a valid exchange rate");
            return;
          }
          // Convert expense amount to account currency
          amountInAccountCurrency = amount * rate;
        } else {
          // Same currency - rate is always 1
          rate = 1;
        }
        
        // Calculate amount in default currency
        // Field stores account-to-default rate, but we need expense-to-default rate for storage
        let amountInDefaultCurrency: number = amount; // fallback for same-currency case
        let defaultCurrencyRate: number | undefined; // This will be expense-to-default rate for storage
        
        if (expenseCurrency !== defaultCurrency) {
          // If defaultCurrencyRate was manually edited, use it directly as expenseToDefaultRate
          // User wants to set the rate directly without any multiplication
          if (defaultCurrencyRateManuallyEdited && formData.defaultCurrencyRate && formData.defaultCurrencyRate.trim() !== "") {
            const parsedRate = parseFloat(formData.defaultCurrencyRate);
            if (!isNaN(parsedRate) && parsedRate > 0 && parsedRate >= 0.0001 && parsedRate <= 10000) {
              defaultCurrencyRate = parsedRate;
              // Calculate amount in default currency directly: expenseAmount * expenseToDefaultRate
              amountInDefaultCurrency = amount * defaultCurrencyRate;
            }
            // If manually edited but value is invalid (e.g. accidental "0", "?", stale data), fall through to use calculated rate instead of blocking
          }
          if (defaultCurrencyRate === undefined) {
            // Auto-calculated or not manually edited - use the two-step conversion
            // Get account-to-default rate: use formData.defaultCurrencyRate if valid, otherwise calculate
            // Special case: if account is already in default currency, rate is 1 (field is hidden in UI)
            let accountToDefaultRate: number;
            const currenciesMatch = expenseCurrency === accountCurrency;
            
            if (accountCurrency === defaultCurrency) {
              accountToDefaultRate = 1;
            } else if (formData.defaultCurrencyRate && formData.defaultCurrencyRate.trim() !== "") {
              const parsedRate = parseFloat(formData.defaultCurrencyRate);
              if (!isNaN(parsedRate) && parsedRate > 0 && parsedRate >= 0.0001 && parsedRate <= 10000) {
                accountToDefaultRate = parsedRate;
              } else {
                // Invalid rate - check if currencies match and rate was manually set
                if (currenciesMatch && formData.rate) {
                  const manualRate = parseFloat(formData.rate);
                  if (!isNaN(manualRate) && manualRate > 0) {
                    // Use the manually edited rate as accountToDefaultRate
                    accountToDefaultRate = manualRate;
                  } else {
                    accountToDefaultRate = accountCurrency !== defaultCurrency 
                      ? calculateTransferRate(accountCurrency, defaultCurrency)
                      : 1;
                  }
                } else {
                  accountToDefaultRate = accountCurrency !== defaultCurrency 
                    ? calculateTransferRate(accountCurrency, defaultCurrency)
                    : 1;
                }
              }
            } else {
              // No defaultCurrencyRate set - check if currencies match and rate was manually set
              if (currenciesMatch && formData.rate) {
                const manualRate = parseFloat(formData.rate);
                if (!isNaN(manualRate) && manualRate > 0) {
                  // Use the manually edited rate as accountToDefaultRate
                  // This ensures expenseToDefaultRate = exchangeRate * accountToDefaultRate = 1 * rate = rate
                  accountToDefaultRate = manualRate;
                } else {
                  // No valid manual rate - use calculated rate from settings
                  accountToDefaultRate = accountCurrency !== defaultCurrency 
                    ? calculateTransferRate(accountCurrency, defaultCurrency)
                    : 1;
                }
              } else {
                // Currencies don't match or no rate set - require defaultCurrencyRate
                if (!currenciesMatch) {
                  alert("Please enter a default currency exchange rate for currency conversion");
                  return;
                }
                // Currencies match but no rate - use calculated rate from settings
                accountToDefaultRate = accountCurrency !== defaultCurrency 
                  ? calculateTransferRate(accountCurrency, defaultCurrency)
                  : 1;
              }
            }
            
            // Field stores account-to-default rate, convert to expense-to-default rate
            // expenseAmount * expenseToDefaultRate = defaultAmount
            // accountAmount * accountToDefaultRate = defaultAmount
            // accountAmount = expenseAmount * exchangeRate
            // So: expenseAmount * expenseToDefaultRate = expenseAmount * exchangeRate * accountToDefaultRate
            // Therefore: expenseToDefaultRate = exchangeRate * accountToDefaultRate
            // Note: When currencies match, rate = 1 (from earlier logic), so expenseToDefaultRate = 1 * accountToDefaultRate = accountToDefaultRate
            defaultCurrencyRate = rate * accountToDefaultRate;
            
            // Calculate amount in default currency: expenseAmount -> accountAmount -> defaultAmount
            const amountInAccountCurrency = amount * rate;
            amountInDefaultCurrency = amountInAccountCurrency * accountToDefaultRate;
          }
        } else {
          // Same currency as default - rate is 1, amount is the same
          defaultCurrencyRate = 1;
          amountInDefaultCurrency = amount;
        }
        
        // Create transaction payload
        const transactionData: any = {
          userId: auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : "demo-user"),
          title: formData.title || formData.category || "",
          category: formData.category,
          type: dialogType === "income" ? "income" : "expense",
          amount: amount, // Store original amount in expense currency
          currency: expenseCurrency,
          rate: rate, // Always include rate (1 for same currency, conversion rate otherwise)
          amountInDefaultCurrency: amountInDefaultCurrency, // Store amount in default currency at transaction time
          defaultCurrencyAtTime: defaultCurrency, // Store what default currency was when created
          isIncome: dialogType === "income",
          date: Timestamp.fromDate(new Date(combineDateAndTime(formData.date, formData.time))),
          note: derived.note || "",
          accountId: formData.accountId,
          accountName: account.accountName, // Store account name for deleted accounts
          createdAt: serverTimestamp(),
        };
        
        // Only include defaultCurrencyRate if currencies differ (Firestore rejects undefined values)
        if (expenseCurrency !== defaultCurrency && defaultCurrencyRate != null && defaultCurrencyRate > 0) {
          transactionData.defaultCurrencyRate = defaultCurrencyRate;
        }
        
        // Add details if present
        if (formData.details && Object.keys(formData.details).length > 0) {
          transactionData.details = formData.details;
        }
        if (derived.attachmentUrl) {
          transactionData.attachmentUrl = derived.attachmentUrl;
          transactionData.attachmentName = derived.attachmentName || "";
          transactionData.attachmentType = derived.attachmentType || "";
        }
        // Custom field values (pending files uploaded after batch)
        if (formData.customFieldValues && Object.keys(formData.customFieldValues).length > 0) {
          transactionData.customFieldValues = { ...formData.customFieldValues };
        }
        
        transactionRef = doc(transactionsRef);
        // New transaction: allocate friendly ID sequence in a transaction, then create doc + update account
        await runTransaction(db, async (transaction) => {
          const settingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
          const settingsSnap = await transaction.get(settingsRef);
          const data = settingsSnap.data();
          const prefix = data?.transactionIdPrefix;
          let seq: number | undefined;
          if (prefix != null && typeof prefix === "string" && prefix.trim() !== "") {
            seq = typeof data?.transactionIdNextSequence === "number" ? data.transactionIdNextSequence : 1;
            transaction.update(settingsRef, { transactionIdNextSequence: seq + 1 });
          }
          const finalData = { ...transactionData };
          if (seq != null) finalData.friendlyIdSequence = seq;
          transaction.set(transactionRef!, finalData);
          transaction.update(accountRef, {
            amount: increment(dialogType === "income" ? amountInAccountCurrency : -amountInAccountCurrency),
          });
          // Expense fee (when workspace allows and user entered fee)
          if (
            dialogType === "expense" &&
            allowExpenseFee &&
            formData.feeAmount &&
            formData.feeAccountId &&
            parseFloat(formData.feeAmount) > 0
          ) {
            const feeAmount = parseFloat(formData.feeAmount);
            const feeAccount = getAccountById(formData.feeAccountId);
            if (feeAccount && !isNaN(feeAmount)) {
              const feeAccountRef = doc(db, "workspaces", workspaceId, "accounts", feeAccount.id);
              transaction.update(feeAccountRef, { amount: increment(-feeAmount) });
              const feeRef = doc(transactionsRef);
              const feeSeq = seq != null ? seq + 1 : undefined;
              if (feeSeq != null) {
                transaction.update(settingsRef, { transactionIdNextSequence: feeSeq + 1 });
              }
              const feeData = {
                id: feeRef.id,
                userId: auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : "demo-user"),
                title: "Expense Fee",
                category: "Expense Fee",
                type: "expense",
                amount: feeAmount,
                currency: feeAccount.currency,
                rate: 1,
                amountInDefaultCurrency: feeAmount,
                defaultCurrencyAtTime: defaultCurrency,
                isIncome: false,
                date: Timestamp.fromDate(new Date(combineDateAndTime(formData.date, formData.time))),
                note: `Fee for expense: ${formData.title || formData.category || ""}`,
                accountId: feeAccount.id,
                accountName: feeAccount.accountName,
                expenseFeeFor: transactionRef!.id,
                createdAt: serverTimestamp(),
                ...(feeSeq != null ? { friendlyIdSequence: feeSeq } : {}),
              };
              transaction.set(feeRef, feeData);
            }
          }
        });
      }

      // Commit batch only when editing (new transactions use runTransaction above)
      if (editingTransaction) {
        await batch.commit();
      }
      
      // Upload custom field attachments (for both new and edit) - then sync derived note/attachment to transaction
      if (transactionRef && formData.customFieldPendingFiles && Object.keys(formData.customFieldPendingFiles).length > 0) {
        setUploadingAttachment(true);
        try {
          const currentDoc = await getDoc(transactionRef);
          const existing = (currentDoc.exists() ? currentDoc.data()?.customFieldValues : undefined) as TransactionCustomFieldValues | undefined;
          const merged: TransactionCustomFieldValues = { ...(formData.customFieldValues || {}), ...(existing || {}) };
          for (const [fieldId, file] of Object.entries(formData.customFieldPendingFiles)) {
            const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
            const path = `workspaces/${workspaceId}/transactions/${transactionRef.id}/custom-fields/${fieldId}_${Date.now()}_${sanitized}`;
            const storageRef = ref(storage, path);
            let contentType = file.type || "application/octet-stream";
            if (contentType === "image/jpg") contentType = "image/jpeg";
            await uploadBytes(storageRef, file, { contentType, customMetadata: { originalFileName: file.name } });
            const url = await getDownloadURL(storageRef);
            const value: CustomFieldAttachmentValue = { url, name: file.name, type: contentType };
            merged[fieldId] = value;
          }
          await updateDoc(transactionRef, { customFieldValues: merged });
          if (transactionDataForHistory) {
            transactionDataForHistory.customFieldValues = merged;
          }
          const afterDerived = deriveNoteAndAttachmentFromCustomFields(transactionCustomFields, dialogType, merged);
          await updateDoc(transactionRef, {
            note: afterDerived.note || "",
            ...(afterDerived.attachmentUrl
              ? {
                  attachmentUrl: afterDerived.attachmentUrl,
                  attachmentName: afterDerived.attachmentName || "",
                  attachmentType: afterDerived.attachmentType || "",
                }
              : {}),
          });
          if (transactionDataForHistory) {
            transactionDataForHistory.note = afterDerived.note || "";
            transactionDataForHistory.attachmentUrl = afterDerived.attachmentUrl ?? transactionDataForHistory.attachmentUrl ?? null;
            transactionDataForHistory.attachmentName = afterDerived.attachmentName ?? transactionDataForHistory.attachmentName ?? null;
          }
        } catch (err) {
          console.error("Error uploading custom field files:", err);
          alert("Transaction saved but some custom field files could not be uploaded. You can edit the transaction to try again.");
        } finally {
          setUploadingAttachment(false);
        }
      }

      // Save transaction history - always save when editing (even if attachment upload failed)
      if (editingTransaction && transactionRef && transactionDataForHistory) {
        // Editing - save changes
        await saveTransactionHistory(editingTransaction.id, editingTransaction, transactionDataForHistory);
      } else if (!editingTransaction && transactionRef) {
        // New transaction - save creation entry
        await saveCreationHistory(transactionRef.id);
      }

      // Invalidate transactions cache and trigger refetch so list shows new/updated fee with id
      invalidateTransactionsCache(workspaceId);
      onTransactionSaved?.();

      // Close dialog and reset form only if no errors occurred
      setDialogOpen(false);
      // Clear newly added detail options and input values after successful transaction creation/update
      setNewlyAddedDetailOptions({});
      setDetailInputValues({});
      setShowTime(false);
      setFormData({
        title: "",
        category: "",
        amount: "",
        currency: defaultCurrency,
        rate: "",
        defaultCurrencyRate: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        accountId: "",
        accountGroup: "",
        customFieldValues: undefined,
        customFieldPendingFiles: undefined,
        feeAmount: undefined,
        feeAccountId: undefined,
      });
      setEditingTransaction(null);
    } catch (error) {
      console.error("Error saving transaction:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("Full error details:", error);
      alert(`Failed to save transaction: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    workspaceId,
    formData,
    editingTransaction,
    dialogType,
    defaultCurrency,
    accounts,
    expenseCategories,
    incomeCategories,
    categoryGroups,
    newlyAddedDetailOptions,
    getAccountById,
    setIsSubmitting,
    setDialogOpen,
    setEditingTransaction,
    setShowTime,
    setFormData,
    setUploadingAttachment,
    setSnackbarElement,
    setSnackbarOpen,
    setNewlyAddedDetailOptions,
    setDetailInputValues,
    saveTransactionHistory,
    saveCreationHistory,
    transactionCustomFields,
  ]);

  // Handle transfer submission
  const handleTransferSubmit = useCallback(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmittingTransfer) {
      return;
    }
    
    // Allow loading in demo mode, bypass auth mode, or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    if (transferFormData.fromAccountId === transferFormData.toAccountId) {
      alert("Cannot transfer to the same account");
      return;
    }

    const amount = parseFloat(transferFormData.amount);
    if (isNaN(amount) || amount < 0) {
      alert("Please enter a valid amount");
      return;
    }

    // DEMO MODE: Block all write operations - demo mode is read-only
    if (isDemoMode()) {
      setSnackbarElement({
        severity: "info",
        message: "Demo mode: Transfers cannot be saved. Please log in to save transfers.",
      });
      setSnackbarOpen(true);
      setTransferDialogOpen(false);
      setEditingTransaction(null);
      setShowTransferTime(false);
      if (setShowTransferFee) {
        setShowTransferFee(false);
      }
      setTransferFormData({
        fromAccountId: "",
        toAccountId: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        transferRate: "",
        feeAmount: undefined,
        feeAccountId: undefined,
      });
      return;
    }

    setIsSubmittingTransfer(true);
    
    try {
      // Ensure user is in workspace members subcollection before saving transfer
      // Ensure user is in workspace members subcollection before saving transfer
      // Skip this check in dev mode (bypass auth enabled)
      if (auth.currentUser && workspaceId && !isBypassAuthEnabled()) {
        const userId = auth.currentUser.uid;
        const memberRef = doc(db, "workspaces", workspaceId, "members", userId);
        const memberSnap = await getDoc(memberRef);
        
        if (!memberSnap.exists()) {
          // Check if user is the workspace owner
          const workspaceRef = doc(db, "workspaces", workspaceId);
          const workspaceSnap = await getDoc(workspaceRef);
          const workspaceData = workspaceSnap.data();
          const isOwner = workspaceSnap.exists() && workspaceData?.ownerId === userId;
          
          if (isOwner) {
            // Add owner to members subcollection
            await setDoc(memberRef, {
              role: "owner",
              joinedAt: serverTimestamp(),
            });
            
            // Verify it was added
            const verifySnap = await getDoc(memberRef);
            if (!verifySnap.exists()) {
              throw new Error("Failed to add user to workspace members. Please try again.");
            }
            
            // Added workspace owner to members subcollection before transfer save
          } else {
            throw new Error("User is not a member of this workspace. Please ask the workspace owner to add you as a member.");
          }
        }
      }
      
      const fromAccount = accounts.find((acc) => acc.id === transferFormData.fromAccountId);
      const toAccount = accounts.find((acc) => acc.id === transferFormData.toAccountId);

      if (!fromAccount || !toAccount) {
        alert("Account not found");
        return;
      }

      // Check if currencies differ
      const currenciesDiffer = fromAccount.currency !== toAccount.currency;
      let transferRate: number;

      if (currenciesDiffer) {
        // Require transfer rate when currencies differ
        if (!transferFormData.transferRate || transferFormData.transferRate.trim() === "") {
          alert("Please enter an exchange rate for currency conversion");
          return;
        }
        transferRate = parseFloat(transferFormData.transferRate);
        if (isNaN(transferRate) || transferRate <= 0) {
          alert("Please enter a valid exchange rate");
          return;
        }
      } else {
        // Same currency - rate is always 1
        transferRate = 1;
      }

      // Validate account amounts are valid numbers
      let fromAccountAmount = parseFloat(String(fromAccount.amount || 0));
      let toAccountAmount = parseFloat(String(toAccount.amount || 0));
      
      if (isNaN(fromAccountAmount) || !isFinite(fromAccountAmount)) {
        alert(`Invalid balance for source account "${fromAccount.accountName}". Current balance: ${fromAccount.amount}. Please refresh and try again.`);
        return;
      }
      
      if (isNaN(toAccountAmount) || !isFinite(toAccountAmount)) {
        alert(`Invalid balance for destination account "${toAccount.accountName}". Current balance: ${toAccount.amount}. Please refresh and try again.`);
        return;
      }

      // Use batch write for atomic updates
      const batch = writeBatch(db);

      // Calculate amount to add to toAccount (convert if currencies differ)
      const toAmount = currenciesDiffer && transferRate ? amount * transferRate : amount;
      
      // Validate calculated amounts are valid
      if (isNaN(toAmount) || !isFinite(toAmount)) {
        alert(`Invalid transfer calculation. Amount: ${amount}, Rate: ${transferRate}, Calculated: ${toAmount}. Please check the exchange rate and try again.`);
        return;
      }

      // Update account balances using atomic increments to prevent race conditions
      const fromAccountRef = doc(db, "workspaces", workspaceId, "accounts", fromAccount.id);
      const toAccountRef = doc(db, "workspaces", workspaceId, "accounts", toAccount.id);
      
      // Handle editing: reverse old transfer's effect on accounts
      if (editingTransaction && editingTransaction.type === "transfer") {
        const oldFromAccountId = editingTransaction.fromAccountId || "";
        const oldToAccountId = editingTransaction.toAccountId || "";
        const oldAmount = editingTransaction.amount || 0;
        const oldTransferRate = editingTransaction.transferRate;
        
        // Get old accounts
        const oldFromAccount = oldFromAccountId ? accounts.find((acc) => acc.id === oldFromAccountId) : null;
        const oldToAccount = oldToAccountId ? accounts.find((acc) => acc.id === oldToAccountId) : null;
        
        if (oldFromAccount && oldToAccount) {
          // Calculate old toAmount (converted amount)
          const oldCurrenciesDiffer = oldFromAccount.currency !== oldToAccount.currency;
          const oldToAmount = oldCurrenciesDiffer && oldTransferRate ? oldAmount * oldTransferRate : oldAmount;
          
          // Reverse old transfer using atomic increments
          const oldFromAccountRef = doc(db, "workspaces", workspaceId, "accounts", oldFromAccount.id);
          const oldToAccountRef = doc(db, "workspaces", workspaceId, "accounts", oldToAccount.id);
          
          // Reverse: add back to fromAccount, subtract from toAccount
          batch.update(oldFromAccountRef, { amount: increment(oldAmount) });
          batch.update(oldToAccountRef, { amount: increment(-oldToAmount) });
          
          // Apply new transfer on new accounts (or same accounts after reversal)
          if (oldFromAccountId === fromAccount.id && oldToAccountId === toAccount.id) {
            // Same accounts - net effect: apply new transfer after reversing old one
            batch.update(fromAccountRef, { amount: increment(-amount) });
            batch.update(toAccountRef, { amount: increment(toAmount) });
          } else {
            // Different accounts - apply new transfer on new accounts
            batch.update(fromAccountRef, { amount: increment(-amount) });
            batch.update(toAccountRef, { amount: increment(toAmount) });
          }
        } else {
          // Old accounts not found - just apply new transfer
          batch.update(fromAccountRef, { amount: increment(-amount) });
          batch.update(toAccountRef, { amount: increment(toAmount) });
        }
      } else {
        // New transfer - apply transfer effect
        batch.update(fromAccountRef, { amount: increment(-amount) });
        batch.update(toAccountRef, { amount: increment(toAmount) });
      }

      // Create or update transfer transaction
      const transactionsRef = collection(db, "workspaces", workspaceId, "transactions");
      const transferDerived = deriveNoteAndAttachmentFromCustomFields(
        transactionCustomFields,
        "transfer",
        transferFormData.customFieldValues
      );
      
      // Build transaction data - always include transferRate (1 for same currency, conversion rate otherwise)
      const transactionData: any = {
        userId: auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : "demo-user"),
        type: "transfer",
        amount: amount, // Store original amount in fromAccount currency
        currency: fromAccount.currency,
        transferRate: transferRate, // Always include rate (1 for same currency, conversion rate otherwise)
        date: Timestamp.fromDate(new Date(combineDateAndTime(transferFormData.date, transferFormData.time))),
        note: transferDerived.note || "",
        fromAccountId: fromAccount.id,
        fromAccountName: fromAccount.accountName, // Store account name for deleted accounts
        toAccountId: toAccount.id,
        toAccountName: toAccount.accountName, // Store account name for deleted accounts
      };
      if (transferDerived.attachmentUrl) {
        transactionData.attachmentUrl = transferDerived.attachmentUrl;
        transactionData.attachmentName = transferDerived.attachmentName || "";
        transactionData.attachmentType = transferDerived.attachmentType || "";
      }
      if (transferFormData.customFieldValues && Object.keys(transferFormData.customFieldValues).length > 0) {
        transactionData.customFieldValues = { ...transferFormData.customFieldValues };
      }
      
      let transferTransactionRef: ReturnType<typeof doc> | null = null;
      if (editingTransaction && editingTransaction.type === "transfer") {
        // Update existing transaction
        transactionData.createdAt = editingTransaction.createdAt;
        // Add updatedAt when editing
        transactionData.updatedAt = serverTimestamp();
        transferTransactionRef = doc(db, "workspaces", workspaceId, "transactions", editingTransaction.id);
        batch.update(transferTransactionRef, transactionData);
      } else {
        // Create new transaction
        transactionData.createdAt = serverTimestamp();
        transferTransactionRef = doc(transactionsRef);
        batch.set(transferTransactionRef, transactionData);
      }

      // Handle fee if present
      let existingFeeTransaction: TransactionWithDate | null = null;
      let existingFeeTransactionId: string | null = null;
      let feeTransactionRef: ReturnType<typeof doc> | null = null;
      let feeTransactionData: any = null;
      
      if (allowTransferFee && transferFormData.feeAmount && transferFormData.feeAccountId) {
        const feeAmount = parseFloat(transferFormData.feeAmount);
        if (!isNaN(feeAmount) && feeAmount > 0) {
          const feeAccount = accounts.find((acc) => acc.id === transferFormData.feeAccountId);
          
          if (!feeAccount) {
            alert("Fee account not found");
            setIsSubmittingTransfer(false);
            return;
          }

          // Validate fee account balance
          let feeAccountAmount = parseFloat(String(feeAccount.amount || 0));
          if (isNaN(feeAccountAmount) || !isFinite(feeAccountAmount)) {
            alert(`Invalid balance for fee account "${feeAccount.accountName}". Please refresh and try again.`);
            setIsSubmittingTransfer(false);
            return;
          }

          // Find existing fee transaction if editing
          let oldFeeAmount = 0;
          let oldFeeAccountId: string | null = null;
          
          if (editingTransaction && editingTransaction.type === "transfer" && transferTransactionRef) {
            // Query for existing fee transaction linked to this transfer
            const feeQuery = query(
              transactionsRef,
              where("transferFeeFor", "==", editingTransaction.id),
              where("type", "==", "expense")
            );
            const feeQuerySnapshot = await getDocs(feeQuery);
            
            if (!feeQuerySnapshot.empty) {
              const feeDoc = feeQuerySnapshot.docs[0];
              const feeData = feeDoc.data();
              existingFeeTransactionId = feeDoc.id;
              oldFeeAmount = feeData.amount || 0;
              oldFeeAccountId = feeData.accountId || null;
              
              // Create TransactionWithDate object for history
              existingFeeTransaction = {
                id: feeDoc.id,
                ...feeData,
                date: feeData.date?.toDate ? feeData.date.toDate() : feeData.date,
              } as TransactionWithDate;
            }
          }

          // Update fee account balance
          const feeAccountRef = doc(db, "workspaces", workspaceId, "accounts", feeAccount.id);
          
          if (editingTransaction && editingTransaction.type === "transfer" && existingFeeTransactionId) {
            // When editing, reverse old fee and apply new fee
            if (oldFeeAccountId && oldFeeAccountId === feeAccount.id) {
              // Same account - adjust balance by difference
              const balanceChange = oldFeeAmount - feeAmount;
              if (balanceChange !== 0) {
                batch.update(feeAccountRef, { amount: increment(balanceChange) });
              }
            } else {
              // Different account or no old account - reverse old fee and apply new fee
              if (oldFeeAccountId) {
                const oldFeeAccountRef = doc(db, "workspaces", workspaceId, "accounts", oldFeeAccountId);
                batch.update(oldFeeAccountRef, { amount: increment(oldFeeAmount) });
              }
              batch.update(feeAccountRef, { amount: increment(-feeAmount) });
            }
          } else {
            // New transfer - just subtract fee
            batch.update(feeAccountRef, { amount: increment(-feeAmount) });
          }

          // Create or update fee expense transaction
          feeTransactionData = {
            userId: auth.currentUser?.uid || (isBypassAuthEnabled() ? "bypass-user" : "demo-user"),
            title: "Transfer Fee",
            category: "Transfer Fee",
            type: "expense",
            amount: feeAmount,
            currency: feeAccount.currency,
            rate: 1, // Fee is always in fee account currency (no conversion needed)
            amountInDefaultCurrency: feeAmount, // Same as amount since rate is 1
            defaultCurrencyAtTime: defaultCurrency,
            isIncome: false,
            date: Timestamp.fromDate(new Date(combineDateAndTime(transferFormData.date, transferFormData.time))),
            note: `Fee for transfer from ${fromAccount.accountName} to ${toAccount.accountName}`,
            accountId: feeAccount.id,
            accountName: feeAccount.accountName,
            transferFeeFor: transferTransactionRef?.id || null, // Link to transfer transaction
          };

          if (editingTransaction && editingTransaction.type === "transfer" && existingFeeTransactionId) {
            // Update existing fee transaction
            feeTransactionData.createdAt = existingFeeTransaction?.createdAt || serverTimestamp();
            feeTransactionData.updatedAt = serverTimestamp();
            feeTransactionRef = doc(db, "workspaces", workspaceId, "transactions", existingFeeTransactionId);
            batch.update(feeTransactionRef, feeTransactionData);
          } else {
            // New transfer - create fee transaction
            feeTransactionData.createdAt = serverTimestamp();
            feeTransactionRef = doc(transactionsRef);
            feeTransactionData.id = feeTransactionRef.id;
            const transferFeeSettingsRef = doc(db, "workspaces", workspaceId, "settings", "general");
            const transferFeeSettingsSnap = await getDoc(transferFeeSettingsRef);
            const transferFeeSettingsData = transferFeeSettingsSnap.data();
            if (transferFeeSettingsData && typeof transferFeeSettingsData.transactionIdPrefix === "string" && transferFeeSettingsData.transactionIdPrefix.trim() !== "") {
              const transferFeeSeq = typeof transferFeeSettingsData.transactionIdNextSequence === "number" ? transferFeeSettingsData.transactionIdNextSequence : 1;
              batch.update(transferFeeSettingsRef, { transactionIdNextSequence: transferFeeSeq + 1 });
              feeTransactionData.friendlyIdSequence = transferFeeSeq;
            }
            batch.set(feeTransactionRef, feeTransactionData);
          }
        } else if (transferFormData.feeAmount === "" || parseFloat(transferFormData.feeAmount) === 0) {
          // Fee removed - need to find and delete existing fee transaction if editing
          if (editingTransaction && editingTransaction.type === "transfer") {
            // Fee removal should be handled separately via fee edit dialog
            // This case is when user sets fee to 0 in transfer dialog
            // We'll handle it by not creating/updating fee transaction
          }
        }
      }

      await batch.commit();

      // Save fee transaction history if updating existing fee
      if (editingTransaction && editingTransaction.type === "transfer" && existingFeeTransaction && existingFeeTransactionId && feeTransactionRef && feeTransactionData) {
        await saveTransactionHistory(existingFeeTransactionId, existingFeeTransaction, feeTransactionData);
        setTransactionHasHistory((prev) => new Set(prev).add(existingFeeTransactionId!));
      }

      // Build transfer history data when editing (save called after custom field uploads so we have final customFieldValues)
      let transferDataForHistory: any = null;
      if (editingTransaction && editingTransaction.type === "transfer" && transferTransactionRef) {
        transferDataForHistory = {
          ...transactionData,
        };
        transferDataForHistory.attachmentUrl = transferDerived.attachmentUrl ?? editingTransaction.attachmentUrl ?? null;
        transferDataForHistory.attachmentName = transferDerived.attachmentName ?? editingTransaction.attachmentName ?? null;
        if (transferFormData.customFieldValues && Object.keys(transferFormData.customFieldValues).length > 0) {
          transferDataForHistory.customFieldValues = { ...transferFormData.customFieldValues };
        } else if (editingTransaction.customFieldValues) {
          transferDataForHistory.customFieldValues = undefined;
        } else {
          transferDataForHistory.customFieldValues = editingTransaction.customFieldValues || undefined;
        }
      } else if (!editingTransaction && transferTransactionRef) {
        await saveCreationHistory(transferTransactionRef.id);
      }

      // Upload transfer custom field attachments, then sync derived note/attachment
      if (transferTransactionRef && transferFormData.customFieldPendingFiles && Object.keys(transferFormData.customFieldPendingFiles).length > 0) {
        setUploadingTransferAttachment(true);
        try {
          const currentDoc = await getDoc(transferTransactionRef);
          const existing = (currentDoc.exists() ? currentDoc.data()?.customFieldValues : undefined) as TransactionCustomFieldValues | undefined;
          const merged: TransactionCustomFieldValues = { ...(transferFormData.customFieldValues || {}), ...(existing || {}) };
          for (const [fieldId, file] of Object.entries(transferFormData.customFieldPendingFiles)) {
            const sanitized = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
            const path = `workspaces/${workspaceId}/transactions/${transferTransactionRef.id}/custom-fields/${fieldId}_${Date.now()}_${sanitized}`;
            const storageRef = ref(storage, path);
            let contentType = file.type || "application/octet-stream";
            if (contentType === "image/jpg") contentType = "image/jpeg";
            await uploadBytes(storageRef, file, { contentType, customMetadata: { originalFileName: file.name } });
            const url = await getDownloadURL(storageRef);
            const value: CustomFieldAttachmentValue = { url, name: file.name, type: contentType };
            merged[fieldId] = value;
          }
          await updateDoc(transferTransactionRef, { customFieldValues: merged });
          if (transferDataForHistory) {
            transferDataForHistory.customFieldValues = merged;
          }
          const afterDerived = deriveNoteAndAttachmentFromCustomFields(transactionCustomFields, "transfer", merged);
          await updateDoc(transferTransactionRef, {
            note: afterDerived.note || "",
            ...(afterDerived.attachmentUrl
              ? {
                  attachmentUrl: afterDerived.attachmentUrl,
                  attachmentName: afterDerived.attachmentName || "",
                  attachmentType: afterDerived.attachmentType || "",
                }
              : {}),
          });
          if (transferDataForHistory) {
            transferDataForHistory.note = afterDerived.note || "";
            transferDataForHistory.attachmentUrl = afterDerived.attachmentUrl ?? transferDataForHistory.attachmentUrl ?? null;
            transferDataForHistory.attachmentName = afterDerived.attachmentName ?? transferDataForHistory.attachmentName ?? null;
          }
        } catch (err) {
          console.error("Error uploading transfer custom field files:", err);
          alert("Transfer saved but some custom field files could not be uploaded. You can edit the transfer to try again.");
        } finally {
          setUploadingTransferAttachment(false);
        }
      }

      // Save transfer edit history (after custom field uploads so we have final customFieldValues)
      if (transferDataForHistory && editingTransaction && transferTransactionRef) {
        await saveTransactionHistory(editingTransaction.id, editingTransaction, transferDataForHistory);
        setTransactionHasHistory((prev) => new Set(prev).add(editingTransaction.id));
      }

      invalidateTransactionsCache(workspaceId);
      onTransactionSaved?.();

      setTransferDialogOpen(false);
      setEditingTransaction(null);
      setShowTransferTime(false);
      if (setShowTransferFee) {
        setShowTransferFee(false);
      }
      setTransferFormData({
        fromAccountId: "",
        toAccountId: "",
        amount: "",
        date: new Date().toISOString().split("T")[0],
        time: "",
        transferRate: "",
        customFieldValues: undefined,
        customFieldPendingFiles: undefined,
        feeAmount: undefined,
        feeAccountId: undefined,
      });
    } catch (error) {
      console.error("Error processing transfer:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      console.error("Full error details:", error);
      alert(`Failed to save transfer: ${errorMessage}`);
    } finally {
      setIsSubmittingTransfer(false);
    }
  }, [
    isSubmittingTransfer,
    workspaceId,
    transferFormData,
    editingTransaction,
    accounts,
    setIsSubmittingTransfer,
    setTransferDialogOpen,
    setEditingTransaction,
    setShowTransferTime,
    setTransferFormData,
    setUploadingTransferAttachment,
    setSnackbarElement,
    setSnackbarOpen,
    saveTransactionHistory,
    saveCreationHistory,
    transactionCustomFields,
    setTransactionHasHistory,
    onTransactionSaved,
    setShowTransferFee,
  ]);

  return {
    handleSubmit,
    handleTransferSubmit,
    saveTransactionHistory,
    saveCreationHistory,
  };
};
